import Alpine from 'alpinejs'
import persist from '@alpinejs/persist'
import collapse from '@alpinejs/collapse'
import flatpickr from 'flatpickr'
import { lazySlideovers } from '../utils/lazy_slideovers'

// Import components
import chart23 from './components/charts/chart-23'
import logisticsTable from './components/logistics-table'

// Import property show page components (registered globally for Turbo Frame compatibility)
import { propertyEnquiries, propertyKeys, propertyInspections, propertyMeters, propertyCertificates } from '../components/property_show_components'
import { timelineCard } from '../components/timeline_card'
import { aiAssistant } from '../components/ai_assistant'
import { epcModal } from '../components/epc_modal'
import { tenancySlideover } from '../components/tenancy_slideover'
import { personSlideover } from '../components/person_slideover'
import { complianceMixin } from '../components/compliance_mixin'
import { accountTabMixin } from '../components/account_tab_mixin'
import { transactionSearchMixin } from '../components/transaction_search_mixin'
import { legacyImportMixin } from '../components/legacy_import_mixin'
import { guaranteeTabMixin } from '../components/guarantee_tab_mixin'
import { addPersonMixin } from '../components/add_person_mixin'
import { withMixins } from '../utils/with_mixins'
import { viewingSlideover } from '../components/viewing_slideover'
import { landlordSlideover } from '../components/landlord_slideover'
import { photoGallery } from '../components/photo_gallery'
import { propertySlideover, ensureModalsLoaded } from '../components/property_slideover'
import { complianceSlideover } from '../components/compliance_slideover'
import { keySlideover } from '../components/key_slideover'
import { contractorSlideover } from '../components/contractor_slideover'
import { issueSlideover } from '../components/issue_slideover'
import { newIssueSlideover } from '../components/new_issue_slideover'
import { maintenanceIndex } from '../components/maintenance_index'
import { universalSearch } from '../components/universal_search'
import { noteEditor } from '../components/note_editor'
import { notesMixin, postNote, destroyNote, unlinkNoteFrom, isUnifiedNoteId } from '../components/notes_mixin'
import { postCertificatePrompt } from '../components/post_certificate_prompt'
import { bankAccountSlideover } from '../components/bank_account_slideover'
import { landlordAccountSlideover } from '../components/landlord_account_slideover'
import { tenancyAccountSlideover } from '../components/tenancy_account_slideover'
import { agencyAccountSlideover } from '../components/agency_account_slideover'
import { depositSchemeAccountSlideover } from '../components/deposit_scheme_account_slideover'
import { suspenseSlideover } from '../components/suspense_slideover'
import { chargeFormSlideover } from '../components/charge_form_slideover'
import { refundFormSlideover } from '../components/refund_form_slideover'
import { rentRefundModal } from '../components/rent_refund_modal'
import { makePaymentsSlideover } from '../components/make_payments_slideover'
import { emailThreadSlideover } from '../components/email_thread_slideover'
import { letterComposer } from '../components/letter_composer'
import { signatureCapture } from '../components/signature_capture'
import { proposedActionCard, proposedActionBundle, groupProposalBundles } from '../components/proposed_action_card'
// The bundle grouper is called from x-for expressions in the shared partial,
// so it must be reachable as a plain global.
window.groupProposalBundles = groupProposalBundles
import { whereThisStands, sortedTimeline, timelineDateLabel, timelineNoteHtml, timelineDaysSince } from '../components/where_this_stands'
// The dashboard's review slide-over renders its own timeline variant (it
// injects the "this approval" row) but shares these formatting brains.
window.timelineHelpers = { sorted: sortedTimeline, dateLabel: timelineDateLabel, noteHtml: timelineNoteHtml, daysSince: timelineDaysSince }
import { addPropertySlideover } from '../components/add_property_slideover'
import { addLandlordSlideover } from '../components/add_landlord_slideover'
import { instructionSlideover } from '../components/instruction_slideover'
import { rentGuaranteeSlideover } from '../components/rent_guarantee_slideover'
import { areaMultiselect } from '../components/area_multiselect'
import { propertyEditModal } from '../components/property_edit_modal'
import { applicantSlideover } from '../components/applicant_slideover'
import { addApplicantSlideover } from '../components/add_applicant_slideover'
import { addValuationSlideover } from '../components/add_valuation_slideover'
import { valuationSlideover } from '../components/valuation_slideover'
import { appraisalSlideover } from '../components/appraisal_slideover'

// CSS imported in main stylesheet

// Alpine.js setup
Alpine.plugin(persist)
Alpine.plugin(collapse)
window.Alpine = Alpine

// $dispatch as Alpine ships it, except that an open-* event sent while a lazily
// loaded slideover shell is still arriving waits for it (see lazy_slideovers.js).
Alpine.magic('dispatch', (el) => (name, detail = {}) =>
  lazySlideovers.send(name, () =>
    el.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true, cancelable: true }))
  )
)
window.complianceMixin = complianceMixin
window.accountTabMixin = accountTabMixin
window.transactionSearchMixin = transactionSearchMixin
window.legacyImportMixin = legacyImportMixin
window.guaranteeTabMixin = guaranteeTabMixin
window.addPersonMixin = addPersonMixin
window.withMixins = withMixins

// Register Alpine.js dropdown component
Alpine.data('dropdown', () => ({
  open: false,
  toggle() {
    this.open = !this.open
  },
  close(event) {
    if (!this.$el.contains(event.target)) {
      this.open = false
    }
  },
  init() {
    // Close on click outside
    document.addEventListener('click', this.close.bind(this))
  },
  destroy() {
    document.removeEventListener('click', this.close.bind(this))
  }
}))

// Register logistics table component
Alpine.data('logisticsTable', logisticsTable)

// Register property show page components (for Turbo Frame compatibility)
Alpine.data('propertyEnquiries', propertyEnquiries)
Alpine.data('propertyKeys', propertyKeys)
Alpine.data('propertyInspections', propertyInspections)
Alpine.data('propertyMeters', propertyMeters)
Alpine.data('propertyCertificates', propertyCertificates)
Alpine.data('timelineCard', timelineCard)
Alpine.data('aiAssistant', aiAssistant)
Alpine.data('epcModal', epcModal)
Alpine.data('areaMultiselect', areaMultiselect)
Alpine.data('propertyEditModal', propertyEditModal)
Alpine.data('applicantSlideover', applicantSlideover)
Alpine.data('addApplicantSlideover', addApplicantSlideover)
Alpine.data('addValuationSlideover', addValuationSlideover)
Alpine.data('valuationSlideover', valuationSlideover)
Alpine.data('appraisalSlideover', appraisalSlideover)
Alpine.data('tenancySlideover', tenancySlideover)
Alpine.data('personSlideover', personSlideover)
Alpine.data('viewingSlideover', viewingSlideover)
Alpine.data('landlordSlideover', landlordSlideover)
Alpine.data('photoGallery', photoGallery)
Alpine.data('propertySlideover', propertySlideover)
Alpine.data('complianceSlideover', complianceSlideover)
Alpine.data('keySlideover', keySlideover)
Alpine.data('contractorSlideover', contractorSlideover)
Alpine.data('issueSlideover', issueSlideover)
Alpine.data('newIssueSlideover', newIssueSlideover)
Alpine.data('maintenanceIndex', maintenanceIndex)
Alpine.data('universalSearch', universalSearch)
Alpine.data('noteEditor', noteEditor)
// Note helpers for components defined inline in views (the application slideover)
window.LetAdminNotes = { notesMixin, postNote, destroyNote, unlinkNoteFrom, isUnifiedNoteId }
Alpine.data('postCertificatePrompt', postCertificatePrompt)
Alpine.data('bankAccountSlideover', bankAccountSlideover)
Alpine.data('landlordAccountSlideover', landlordAccountSlideover)
Alpine.data('tenancyAccountSlideover', tenancyAccountSlideover)
Alpine.data('agencyAccountSlideover', agencyAccountSlideover)
Alpine.data('depositSchemeAccountSlideover', depositSchemeAccountSlideover)
Alpine.data('suspenseSlideover', suspenseSlideover)
Alpine.data('chargeFormSlideover', chargeFormSlideover)
Alpine.data('refundFormSlideover', refundFormSlideover)
Alpine.data('rentRefundModal', rentRefundModal)
Alpine.data('makePaymentsSlideover', makePaymentsSlideover)
Alpine.data('emailThreadSlideover', emailThreadSlideover)
Alpine.data('letterComposer', letterComposer)
Alpine.data('signatureCapture', signatureCapture)
Alpine.data('proposedActionCard', proposedActionCard)
Alpine.data('proposedActionBundle', proposedActionBundle)
Alpine.data('whereThisStands', whereThisStands)
Alpine.data('addPropertySlideover', addPropertySlideover)
Alpine.data('addLandlordSlideover', addLandlordSlideover)
Alpine.data('instructionSlideover', instructionSlideover)
Alpine.data('rentGuaranteeSlideover', rentGuaranteeSlideover)

// Initialize components function (called on both DOMContentLoaded and turbo:load)
function initializeComponents() {
  // Initialize charts
  chart23()

  // Note: Flatpickr is now handled by Stimulus datepicker_controller.js
  // This provides proper lifecycle management for Turbo Frame navigation

  // Search functionality
  const searchInput = document.getElementById('search-input')
  const searchButton = document.getElementById('search-button')

  if (searchInput && searchButton) {
    function focusSearchInput() {
      searchInput.focus()
    }

    searchButton.addEventListener('click', focusSearchInput)

    // Cmd+K or Ctrl+K shortcut
    document.addEventListener('keydown', function(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        focusSearchInput()
      }
    })

    // "/" shortcut
    document.addEventListener('keydown', function(event) {
      if (event.key === '/' && document.activeElement !== searchInput) {
        event.preventDefault()
        focusSearchInput()
      }
    })
  }
}

// Initialize components when DOM is ready (initial page load)
document.addEventListener('DOMContentLoaded', initializeComponents)

// Re-initialize components on Turbo navigation
document.addEventListener('turbo:load', initializeComponents)

// Make flatpickr globally available for Alpine.js x-init
window.flatpickr = flatpickr

// Make formatTimeRemaining globally available for Alpine.js expressions
window.formatTimeRemaining = function(days) {
  if (days < 0) {
    return null; // Expired - don't show remaining time
  }

  // Less than 60 days - show exact days
  if (days < 60) {
    return `${days} ${days === 1 ? 'day' : 'days'} remaining`;
  }

  // 60 days to less than 2 years - show months
  if (days < 730) { // 730 days = ~2 years
    const months = Math.round(days / 30.44); // Average days per month
    return `About ${months} ${months === 1 ? 'month' : 'months'} remaining`;
  }

  // 2 years or more - show years (rounded down with "over" prefix)
  const years = Math.floor(days / 365.25); // Account for leap years, round down
  return `Over ${years} ${years === 1 ? 'year' : 'years'} remaining`;
}

// Global clipboard functionality
window.copyToClipboard = function(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(function(err) {
      console.error('Failed to copy to clipboard:', err)
      fallbackCopy(text)
    })
  } else {
    fallbackCopy(text)
  }
}

// Fallback copy method for older browsers
function fallbackCopy(text) {
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.style.position = 'fixed'
  textArea.style.left = '-999999px'
  textArea.style.top = '-999999px'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()
  try {
    document.execCommand('copy')
  } catch (err) {
    console.error('Fallback copy failed:', err)
  }
  document.body.removeChild(textArea)
}

// Initialize modals store (used across multiple pages)
Alpine.store('modals', {
  isPropertyEditModalOpen: false,
  isPhotoManageModalOpen: false,
  isAdvertisingModalOpen: false,
  isEpcModalOpen: false,
  isLandlordModalOpen: false,
  showManageMetersModal: false,
  showManageUtilitySuppliersModal: false,
  showAddReadingModal: false,
  // Certificate modals
  isCertificateModalOpen: false,
  isCertificateDocumentsModalOpen: false,
  certificateModalMode: 'create', // 'create' or 'edit'
  currentCertificate: null,
  // EPC modal context (for reusable modal across property show and EPC index)
  epcModalPropertyId: null,
  epcModalPropertyAddress: null,
  epcModalCurrentEpc: null,
  // Edit advert modal context
  isEditAdvertModalOpen: false,
  editAdvertPropertyId: null,
  // Photo gallery
  isPhotoGalleryOpen: false,
  photoGalleryPhotos: [],
  photoGalleryCurrentIndex: 0
})

// Initialize slideover z-index manager
// This allows slideovers to stack properly - whichever opens most recently is on top
// Also tracks open slideovers to prevent duplicates and bring existing ones to front
// Note: baseZIndex must be higher than header z-indexes (header: z-99999, hamburger: z-[100002])
Alpine.store('slideovers', {
  baseZIndex: 100010,
  currentTopZIndex: 100010,
  // Track open slideovers: { 'person-123': { component, zIndex }, 'tenancy-456': {...} }
  openSlideovers: {},

  // Call this when opening a slideover to get the z-index it should use
  getNextZIndex() {
    this.currentTopZIndex += 10
    return this.currentTopZIndex
  },

  // Register an open slideover
  register(type, id, component) {
    const key = `${type}-${id}`
    this.openSlideovers[key] = { component, zIndex: component.zIndex }
  },

  // Unregister when closing
  unregister(type, id) {
    const key = `${type}-${id}`
    delete this.openSlideovers[key]
  },

  // Check if a slideover is already open
  isOpen(type, id) {
    const key = `${type}-${id}`
    return !!this.openSlideovers[key]
  },

  // Bring an existing slideover to the front
  bringToFront(type, id) {
    const key = `${type}-${id}`
    const entry = this.openSlideovers[key]
    if (entry && entry.component) {
      entry.component.zIndex = this.getNextZIndex()
      entry.zIndex = entry.component.zIndex
      return true
    }
    return false
  },

  // Reset when all slideovers are closed
  reset() {
    this.currentTopZIndex = this.baseZIndex
    this.openSlideovers = {}
  }
})

// Global function to open the Edit Advert modal for a property
window.openEditAdvertModal = async function(propertyId) {
  await ensureModalsLoaded()
  const store = Alpine.store('modals')
  store.editAdvertPropertyId = propertyId
  const slideoverStore = Alpine.store?.('slideovers')
  store._editAdvertModalZIndex = slideoverStore?.getNextZIndex?.() || 200000
  store.isEditAdvertModalOpen = true
}

// Global function to open EPC modal with property context
window.openEpcModal = async function(propertyId, propertyAddress, currentEpc = null) {
  await ensureModalsLoaded()
  const store = Alpine.store('modals')
  store.epcModalPropertyId = propertyId
  store.epcModalPropertyAddress = propertyAddress
  store.epcModalCurrentEpc = currentEpc
  const slideoverStore = Alpine.store?.('slideovers')
  store._epcModalZIndex = slideoverStore?.getNextZIndex?.() || 200000
  store.isEpcModalOpen = true
}

// Global function to open photo gallery
// photos: array of { url: string, thumbnail_url?: string, alt?: string }
// startIndex: index of the photo to start on (default 0)
window.openPhotoGallery = async function(photos, startIndex = 0) {
  await ensureModalsLoaded()
  Alpine.store('modals').photoGalleryPhotos = photos
  Alpine.store('modals').photoGalleryCurrentIndex = startIndex
  Alpine.store('modals').isPhotoGalleryOpen = true
}

// Start Alpine
Alpine.start()

// Export for use in other modules if needed
export { Alpine }
